/* V212 — AI Agent Plugin Marketing Funnel.
 * URL: /plugin/funnel
 * 4-step conversion flow:
 *   1. URL + agent pick → compat scan
 *   2. Result review
 *   3. Email capture (trial signup)
 *   4. Redirect to dashboard / pricing
 */
import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const AGENTS_SHOWCASE = [
  { slug: "helpdesk", name: "Helpdesk", price: 79, emoji: "💬" },
  { slug: "cart-recovery", name: "Cart Recovery", price: 89, emoji: "🛒" },
  { slug: "review-reply", name: "Review Reply", price: 69, emoji: "⭐" },
  { slug: "newsletter", name: "Newsletter", price: 79, emoji: "📧" },
  { slug: "product-desc", name: "Product Desc", price: 69, emoji: "📝" },
  { slug: "gdpr-watch", name: "GDPR Watch", price: 99, emoji: "🛡️" },
];

const STEPS = ["Scan", "Result", "Trial", "Done"];

export default function AgentPluginFunnel() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [url, setUrl] = useState("");
  const [agent, setAgent] = useState("helpdesk");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [scanResult, setScanResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    // Save attempt in localStorage for resume
    if (url || email) {
      localStorage.setItem("plugin_funnel", JSON.stringify({ step, url, email, agent }));
    }
  }, [step, url, email, agent]);

  const scan = async () => {
    setErr(""); setLoading(true);
    try {
      const r = await axios.post(`${API}/agent-compat/scan`, {
        url: url.startsWith("http") ? url : "https://" + url,
        agent_slug: agent,
      });
      setScanResult(r.data);
      setStep(1);
    } catch (e) {
      setErr(e.response?.data?.detail || "Skenovanie zlyhalo");
    } finally { setLoading(false); }
  };

  const submitEmail = async () => {
    setErr(""); setLoading(true);
    try {
      // Re-scan with email captured for lead-gen
      await axios.post(`${API}/agent-compat/scan`, {
        url: url.startsWith("http") ? url : "https://" + url,
        agent_slug: agent,
        email: email,
      });
      // Track in agent waitlist (existing endpoint)
      try {
        await axios.post(`${API}/agents/waitlist`, {
          agent_slug: agent, email, locale: navigator.language.slice(0, 2),
        });
      } catch (_) { /* non-blocking */ }
      setStep(3);
    } catch (e) {
      setErr(e.response?.data?.detail || "Submit zlyhal");
    } finally { setLoading(false); }
  };

  const incompatible = scanResult?.verdict === "incompatible";

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" data-testid="plugin-funnel">
      <Helmet>
        <title>Spusti AI agenta na svojom webe · TopRankPulse</title>
      </Helmet>

      {/* Stepper */}
      <header className="border-b border-zinc-800 py-4 px-6">
        <div className="max-w-3xl mx-auto flex items-center justify-between gap-3 flex-wrap">
          <Link to="/" className="font-black text-lg">TopRankPulse</Link>
          <div className="flex items-center gap-2 text-xs">
            {STEPS.map((label, i) => (
              <div key={label} className="flex items-center gap-2">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center font-black text-[10px] ${
                  i === step ? "bg-purple-500 text-black" :
                  i < step ? "bg-emerald-500 text-black" :
                  "bg-zinc-800 text-zinc-500"
                }`} data-testid={`step-indicator-${i}`}>
                  {i < step ? "✓" : i + 1}
                </div>
                <span className={i === step ? "text-purple-300 font-bold" : "text-zinc-500"}>{label}</span>
                {i < STEPS.length - 1 && <span className="text-zinc-700">·</span>}
              </div>
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">

        {/* STEP 0 — URL & Agent */}
        {step === 0 && (
          <div data-testid="funnel-step-0">
            <h1 className="text-3xl sm:text-4xl font-black mb-3">
              Pridaj <span className="text-purple-400">AI agenta</span> na svoju stránku za 3 minúty
            </h1>
            <p className="text-zinc-400 mb-8">
              Najprv overíme či funguje. Bez registrácie, bez kreditky.
            </p>

            <div className="bg-zinc-900 border border-zinc-800 p-6 mb-6">
              <label className="text-xs uppercase tracking-widest text-zinc-500 font-bold mb-2 block">
                1. Tvoja URL
              </label>
              <input value={url} onChange={(e) => setUrl(e.target.value)}
                     placeholder="https://eshop.sk"
                     data-testid="funnel-url-input"
                     className="w-full bg-black border border-zinc-700 px-4 py-3 text-sm font-mono focus:border-purple-500 outline-none mb-4" />

              <label className="text-xs uppercase tracking-widest text-zinc-500 font-bold mb-2 block">
                2. Vyber agenta
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-2">
                {AGENTS_SHOWCASE.map((a) => (
                  <button key={a.slug} type="button"
                          onClick={() => setAgent(a.slug)}
                          data-testid={`funnel-agent-${a.slug}`}
                          className={`p-3 text-left border-2 transition ${
                            agent === a.slug
                              ? "bg-purple-500/20 border-purple-500"
                              : "bg-black border-zinc-800 hover:border-zinc-600"
                          }`}>
                    <div className="text-2xl mb-1">{a.emoji}</div>
                    <div className="font-bold text-sm">{a.name}</div>
                    <div className="text-xs text-zinc-500">€{a.price}/mes</div>
                  </button>
                ))}
              </div>
              <div className="text-xs text-zinc-500 mt-2">+ 9 ďalších agentov vidíš v marketplace</div>
            </div>

            {err && <div className="bg-rose-950/40 border border-rose-700 text-rose-300 p-3 text-sm mb-4">{err}</div>}

            <button onClick={scan} disabled={!url || loading}
                    data-testid="funnel-scan-btn"
                    className="w-full bg-purple-500 hover:bg-purple-400 disabled:opacity-50 text-black font-black uppercase tracking-wider px-6 py-4">
              {loading ? "Skenujem stránku…" : "🔍 Skenovať a pokračovať"}
            </button>
          </div>
        )}

        {/* STEP 1 — Result */}
        {step === 1 && scanResult && (
          <div data-testid="funnel-step-1">
            <h1 className="text-3xl font-black mb-3" data-testid="funnel-verdict">
              {scanResult.verdict_label}
            </h1>
            <p className="text-zinc-400 mb-6">{scanResult.verdict_message}</p>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-6">
              <Info label="Doména" value={scanResult.domain || "—"} />
              <Info label="CMS" value={scanResult.cms_detected?.name || "—"} />
              <Info label="Jazyk" value={(scanResult.language || "—").toUpperCase()} />
              <Info label="Agentov live" value="15 / 15" />
            </div>

            {scanResult.install_method && (
              <div className="bg-zinc-900 border border-zinc-800 p-5 mb-6">
                <h3 className="font-black mb-2">📦 Pre tvoju stránku odporúčame:</h3>
                <p className="text-sm text-zinc-300 mb-2">{scanResult.install_method.label}</p>
                <p className="text-xs text-zinc-500">~{scanResult.install_method.time_minutes} min · {
                  scanResult.install_method.difficulty === "easy" ? "🟢 Jednoduché" :
                  scanResult.install_method.difficulty === "medium" ? "🟡 Stredné" : "🔴 Náročné"
                }</p>
              </div>
            )}

            <div className="flex gap-2 flex-wrap">
              {!incompatible && (
                <button onClick={() => setStep(2)}
                        data-testid="funnel-continue-btn"
                        className="bg-purple-500 hover:bg-purple-400 text-black font-black uppercase tracking-wider px-6 py-3 text-sm">
                  ▶︎ Pokračovať na trial
                </button>
              )}
              <button onClick={() => { setStep(0); setScanResult(null); }}
                      data-testid="funnel-rescan-btn"
                      className="border border-zinc-700 text-zinc-300 hover:bg-zinc-900 font-black uppercase tracking-wider px-6 py-3 text-sm">
                🔄 Skúsiť inú URL
              </button>
            </div>
            {incompatible && (
              <div className="mt-6 bg-rose-950/40 border border-rose-700 text-rose-300 p-4 text-sm">
                Tvoja platforma sa zatiaľ nepodporuje. Zanechaj email a dáme ti vedieť keď ju pridáme.
                <button onClick={() => setStep(2)} className="block mt-3 underline text-rose-200">
                  Zaregistrovať záujem →
                </button>
              </div>
            )}
          </div>
        )}

        {/* STEP 2 — Email capture */}
        {step === 2 && (
          <div data-testid="funnel-step-2">
            <h1 className="text-3xl font-black mb-3">Začni 14-day trial 🚀</h1>
            <p className="text-zinc-400 mb-8">
              Žiadny credit card teraz. Vyplň email a meno, dostaneš link na aktiváciu agenta.
            </p>

            <div className="bg-zinc-900 border border-zinc-800 p-6 mb-6 space-y-3">
              <input value={name} onChange={(e) => setName(e.target.value)}
                     placeholder="Tvoje meno"
                     data-testid="funnel-name-input"
                     className="w-full bg-black border border-zinc-700 px-4 py-3 text-sm focus:border-purple-500 outline-none" />
              <input value={email} onChange={(e) => setEmail(e.target.value)}
                     placeholder="tvoj@email.sk"
                     type="email"
                     data-testid="funnel-email-input"
                     className="w-full bg-black border border-zinc-700 px-4 py-3 text-sm focus:border-purple-500 outline-none" />
              <div className="text-xs text-zinc-500">
                ✓ 14 dní zadarmo · ✓ 100 behov / mes free tier · ✓ Zruš kedykoľvek
              </div>
            </div>

            {err && <div className="bg-rose-950/40 border border-rose-700 text-rose-300 p-3 text-sm mb-4">{err}</div>}

            <button onClick={submitEmail} disabled={!email || loading}
                    data-testid="funnel-submit-email"
                    className="w-full bg-purple-500 hover:bg-purple-400 disabled:opacity-50 text-black font-black uppercase tracking-wider px-6 py-4">
              {loading ? "Odosielam…" : "✓ Aktivovať trial"}
            </button>
          </div>
        )}

        {/* STEP 3 — Done */}
        {step === 3 && (
          <div data-testid="funnel-step-3" className="text-center">
            <div className="text-6xl mb-4">🎉</div>
            <h1 className="text-4xl font-black mb-3">Hotovo!</h1>
            <p className="text-zinc-400 mb-8">
              Poslali sme ti email s aktivačným linkom. Po klikntí ťa zavedieme rovno do dashboard-u na vygenerovanie API kľúča.
            </p>
            <div className="bg-purple-500/10 border border-purple-500/30 p-5 mb-8 text-sm">
              <strong className="text-purple-300">Tvoja prvá objednávka:</strong>{" "}
              {AGENTS_SHOWCASE.find((a) => a.slug === agent)?.name || agent} na <code>{url}</code>
            </div>
            <div className="flex gap-2 justify-center flex-wrap">
              <button onClick={() => navigate("/login?email=" + encodeURIComponent(email))}
                      data-testid="funnel-go-login"
                      className="bg-purple-500 hover:bg-purple-400 text-black font-black uppercase tracking-wider px-6 py-3 text-sm">
                Prihlásiť sa do dashboard-u →
              </button>
              <Link to={`/plugin/agent/${agent}`}
                    data-testid="funnel-view-agent"
                    className="border border-zinc-700 text-zinc-300 hover:bg-zinc-900 font-black uppercase tracking-wider px-6 py-3 text-sm">
                📖 Viac o agentovi
              </Link>
            </div>
          </div>
        )}

      </main>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="bg-zinc-900 border border-zinc-800 p-3">
      <div className="text-[9px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="text-sm font-bold text-zinc-100 mt-1 truncate">{value}</div>
    </div>
  );
}
